/**
 * QA dashboard — quality & compliance. Uses the SS audit API and the visits
 * list where accessible (visits may 403 for QA — handled gracefully).
 * Honest "Planned — M10" cards for incident reports, surveys & CAPA. No fake data.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { visitAPI } from '../../../services/api';
import { ssAuditAPI } from '../../../services/ssApi';
import Skeleton from '../../../components/common/Skeleton';
import {
  T, FONT, CARD, I, firstNameOf, greeting, asList,
  FadeSection, Hero, KpiCard, ChartCard, EmptyState,
  PlannedPill, ActionLink, PolarStyles,
} from '../shared';

export default function QADashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [auditEvents, setAuditEvents] = useState(null); // null = unavailable
  const [visits, setVisits] = useState(null);           // null = 403/unavailable

  const load = useCallback(() => {
    setLoading(true);
    const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString();
    Promise.allSettled([
      ssAuditAPI.events({ limit: 100, since: weekAgo }).catch(() => null),
      visitAPI.list({ limit: 100 }).catch(() => null),
    ]).then(([a, v]) => {
      if (a.status === 'fulfilled' && a.value) setAuditEvents(asList(a.value));
      if (v.status === 'fulfilled' && v.value) setVisits(asList(v.value));
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.KPIGrid count={3} cols={3} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const completed = visits ? visits.filter(v => String(v.status || '').toUpperCase() === 'COMPLETED').length : null;
  const closedCount = visits
    ? visits.filter(v => ['COMPLETED', 'CANCELLED', 'NO_SHOW', 'MISSED'].includes(String(v.status || '').toUpperCase())).length
    : null;
  const badCount = visits
    ? visits.filter(v => ['CANCELLED', 'NO_SHOW', 'MISSED'].includes(String(v.status || '').toUpperCase())).length
    : null;
  const cancelRate = closedCount ? Math.round((badCount / closedCount) * 100) : null;

  const kpis = [
    {
      title: 'Audit Events (7d)',
      value: auditEvents ? auditEvents.length : 0,
      color: T.info, Icon: I.shield,
      sub: auditEvents ? 'recorded this week' : 'audit feed unavailable',
    },
    {
      title: 'Completed Visits',
      value: completed ?? 0,
      color: T.green, Icon: I.check,
      sub: visits ? 'quality denominator' : 'visit data not accessible for QA',
    },
    {
      title: 'Cancellation / No-show',
      value: cancelRate ?? 0,
      displayValue: cancelRate !== null ? `${cancelRate}%` : '—',
      color: T.warning, Icon: I.alert,
      sub: cancelRate !== null ? `${badCount} of ${closedCount} closed visits` : 'insufficient data',
    },
  ];

  const planned = [
    { Icon: I.alert,     color: T.danger,  title: 'Incident Reports',     desc: 'Log, triage and track clinical & safety incidents through resolution.' },
    { Icon: I.star,      color: T.warning, title: 'Satisfaction Surveys', desc: 'Patient & family feedback collection with NPS and trend tracking.' },
    { Icon: I.clipboard, color: T.teal,    title: 'Corrective Actions',   desc: 'CAPA workflows linking findings to actions, owners and due dates.' },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Quality assurance"
        title={`${greeting()}, ${firstName} 🛡️`}
        subtitle="Quality and compliance oversight — audit activity, visit quality and upcoming QA tooling."
        stats={[
          { label: 'Audit events', value: auditEvents ? auditEvents.length : '—', Icon: I.shield },
          { label: 'Completed visits', value: completed ?? '—', Icon: I.check },
        ]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      <FadeSection index={2} style={{ marginBottom: 18 }}>
        <ChartCard
          title="Compliance Monitoring"
          subtitle="Audit trail, data-protection and standards conformance"
          action={<ActionLink label="Open Compliance" onClick={() => navigate('/compliance')} />}
        >
          {auditEvents && auditEvents.length ? (
            <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6 }}>
              {auditEvents.length} audit event{auditEvents.length !== 1 ? 's' : ''} captured in the last 7 days.
              Review the full immutable trail, table coverage and access logs in the Compliance module.
            </div>
          ) : (
            <EmptyState icon={I.shield} color={T.info} title="No audit activity to summarise" caption="System changes are recorded automatically. Open the Compliance module for the full audit trail." />
          )}
        </ChartCard>
      </FadeSection>

      <FadeSection index={3} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 18 }}>
        {planned.map(({ Icon, color, title, desc }) => (
          <div key={title} style={{ ...CARD, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, background: `${color}14` }}>
                <Icon size={20} color={color} />
              </span>
              <PlannedPill label="Planned — Compliance module (M10)" />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>{desc}</div>
            </div>
          </div>
        ))}
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
